import { api, bff, publicApi } from './http'
import type {
  Billing,
  BillingPreview,
  BillingStatus,
  CalendarResponse,
  Client,
  CreateInviteResponse,
  FakturoidSubject,
  ImportPreview,
  ImportResult,
  Invoice,
  Project,
  ReportSummary,
  Settings,
  SettingsInput,
  TimeEntry,
  TodaySummary,
  User,
} from './types'

// --- auth (BFF session + backend identity) ---
export const authApi = {
  login: (email: string, password: string, rememberMe = true) =>
    bff.post('/auth/login', { email, password, rememberMe }),
  logout: () => bff.post('/auth/logout'),
  me: () => api.get<User>('/app/users/me').then((r) => r.data),
}

// --- public invite ---
export const inviteApi = {
  info: (token: string) =>
    publicApi.get<{ email: string; firstName: string | null; lastName: string | null }>(`/invite/${token}`).then((r) => r.data),
  redeem: (token: string, password: string) => publicApi.post('/invite/redeem', { token, password }),
}

// --- users ---
export const usersApi = {
  list: () => api.get<User[]>('/app/users').then((r) => r.data),
  createInvite: (body: { email: string; firstName?: string; lastName?: string; isAdmin?: boolean }) =>
    api.post<CreateInviteResponse>('/app/users', body).then((r) => r.data),
  update: (id: string, body: Partial<Pick<User, 'firstName' | 'lastName' | 'isAdmin' | 'status'>>) =>
    api.patch<User>(`/app/users/${id}`, body).then((r) => r.data),
}

// --- clients ---
export const clientsApi = {
  list: (includeArchived = false) =>
    api.get<Client[]>('/app/clients', { params: { includeArchived } }).then((r) => r.data),
  create: (body: ClientBody) => api.post<Client>('/app/clients', body).then((r) => r.data),
  update: (id: string, body: ClientBody) => api.put<Client>(`/app/clients/${id}`, body).then((r) => r.data),
  archive: (id: string) => api.post<Client>(`/app/clients/${id}/archive`).then((r) => r.data),
  unarchive: (id: string) => api.post<Client>(`/app/clients/${id}/unarchive`).then((r) => r.data),
  pairSubject: (id: string, subjectId: number, subjectName?: string | null) =>
    api.put<Client>(`/app/clients/${id}/fakturoid-subject`, { subjectId, subjectName }).then((r) => r.data),
  unpairSubject: (id: string) =>
    api.delete<Client>(`/app/clients/${id}/fakturoid-subject`).then((r) => r.data),
  createSubject: (id: string) =>
    api.post<Client>(`/app/clients/${id}/fakturoid-subject`).then((r) => r.data),
}

/** `monthlyHours`: měsíční úvazek; null = klient ho nesleduje. */
export interface ClientBody {
  name: string
  contact?: string
  note?: string
  monthlyHours?: number | null
}

// --- Fakturoid (jen čtení pro párování) ---
export const fakturoidApi = {
  subjects: (query?: string) =>
    api.get<FakturoidSubject[]>('/app/fakturoid/subjects', { params: { query } }).then((r) => r.data),
}

// --- projects ---
export const projectsApi = {
  list: (params?: { clientId?: string; includeArchived?: boolean }) =>
    api.get<Project[]>('/app/projects', { params }).then((r) => r.data),
  create: (body: { clientId: string; name: string; isBillable: boolean; defaultRate: number; currency: string }) =>
    api.post<Project>('/app/projects', body).then((r) => r.data),
  update: (id: string, body: { clientId: string; name: string; isBillable: boolean; defaultRate: number; currency: string }) =>
    api.put<Project>(`/app/projects/${id}`, body).then((r) => r.data),
  archive: (id: string) => api.post<Project>(`/app/projects/${id}/archive`).then((r) => r.data),
  unarchive: (id: string) => api.post<Project>(`/app/projects/${id}/unarchive`).then((r) => r.data),
}

// --- time entries ---
export const timeEntriesApi = {
  running: () =>
    api.get<TimeEntry>('/app/time-entries/running', { validateStatus: (s) => s === 200 || s === 204 })
      .then((r) => (r.status === 204 ? null : r.data)),
  start: (projectId: string, description?: string) =>
    api.post<TimeEntry>('/app/time-entries/start', { projectId, description }).then((r) => r.data),
  stop: () => api.post<TimeEntry>('/app/time-entries/stop').then((r) => r.data),
  createManual: (body: { projectId: string; description?: string; startTime: string; endTime: string }) =>
    api.post<TimeEntry>('/app/time-entries', body).then((r) => r.data),
  list: (from: string, to: string, projectId?: string) =>
    api.get<TimeEntry[]>('/app/time-entries', { params: { from, to, projectId } }).then((r) => r.data),
  update: (id: string, body: Partial<{ projectId: string; description: string; startTime: string; endTime: string }>) =>
    api.put<TimeEntry>(`/app/time-entries/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/app/time-entries/${id}`),
  importPreview: (body: ImportCsvBody) =>
    api.post<ImportPreview>('/app/time-entries/import/preview', body).then((r) => r.data),
  import: (body: ImportCsvBody) =>
    api.post<ImportResult>('/app/time-entries/import', body).then((r) => r.data),
}

/** `projectMap`: název projektu z CSV -> id projektu v tajmtrekru. */
export interface ImportCsvBody {
  csv: string
  projectMap?: Record<string, string>
  skipDuplicates?: boolean
}

// --- nastavení fakturace (admin) ---
export const settingsApi = {
  get: () => api.get<Settings>('/app/settings').then((r) => r.data),
  update: (body: SettingsInput) => api.put<Settings>('/app/settings', body).then((r) => r.data),
  testFakturoid: () =>
    api.post<{ ok: boolean; message: string }>('/app/settings/fakturoid/test').then((r) => r.data),
}

// --- vyúčtování + faktury (admin) ---
export const billingsApi = {
  preview: (projectId: string, from: string, to: string) =>
    api.get<BillingPreview>('/app/billings/preview', { params: { projectId, from, to } }).then((r) => r.data),
  list: (status?: BillingStatus) => api.get<Billing[]>('/app/billings', { params: { status } }).then((r) => r.data),
  create: (body: { projectId: string; periodFrom: string; periodTo: string; billedHours?: number; note?: string }) =>
    api.post<Billing>('/app/billings', body).then((r) => r.data),
  update: (id: string, body: { billedHours?: number; note?: string }) =>
    api.put<Billing>(`/app/billings/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/app/billings/${id}`),
  invoices: () => api.get<Invoice[]>('/app/billings/invoices').then((r) => r.data),
  createInvoice: (billingIds: string[], taxableFulfillmentDue?: string) =>
    api.post<Invoice>('/app/billings/invoices', { billingIds, taxableFulfillmentDue }).then((r) => r.data),
  removeInvoice: (id: string) => api.delete(`/app/billings/invoices/${id}`),
}

// --- calendar ---
export const calendarApi = {
  range: (from: string, to: string, userId?: string) =>
    api.get<CalendarResponse>('/app/calendar', { params: { from, to, userId } }).then((r) => r.data),
}

// --- souhrn nad Dneškem ---
export const dashboardApi = {
  today: () => api.get<TodaySummary>('/app/dashboard/today').then((r) => r.data),
}

// --- reports ---
export const reportsApi = {
  summary: (from: string, to: string, userId?: string, clientId?: string) =>
    api.get<ReportSummary>('/app/reports/summary', { params: { from, to, userId, clientId } }).then((r) => r.data),
}
